// Card 01, water-lining (R548, ui-map-interface.md §17.3.01).
//
// The engraver hatched every coast with parallel lines that follow it at a
// growing spacing and a fading weight, so the water read as water before any
// tint. Here the lines are iso-lines of the terrain plane's distance from
// rock, which the terrain shader already holds, drawn round every ridge. The
// claim half of the card read the margin of the territory field of R431,
// and it left the tree with that border (R532).
//
// **THIS PIECE OWNS NO BATCH.** It is the one piece of the roster that draws
// nothing itself. Its `rebuild` writes five uniforms into the terrain
// program through `ChartScene`; its `emit` writes no instance at all. A picture that is per-pixel belongs in
// the pass that already reads the field it is made of, and a batch of
// several thousand traced segments a world would be the wrong answer twice
// over (§10 rule 1).
//
// **THE OFF SIDE IS THE SCENE'S.** The controller never calls a piece the
// gate has dropped, so a piece that owns a uniform cannot put it back by
// itself. `MapScene.setVisibility` writes the terms off whenever this piece
// is not showing, which is one compare a frame beside the calls that already
// write the reading and the film flag.
//
// **BOTH READINGS, the same lines.** The card drew the water-lining as an
// engraver's mark, and the piece shipped historical-only; the owner asked
// for the same lines in the modern reading on 2026-09-05, so `chart.toml`
// says `on = true` for both. The figures are the shared `arr`, so the two
// readings cannot drift apart.

import { CHART } from '../../../gen/presentation';
import type { ChartContext, ChartSinks, Piece } from '../context';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.water_lines;

/** What the terrain program is told, straight out of the pack. */
export const WATER_LINE_CFG = {
  d: T.arr.ridge_d,
  alpha: T.arr.ridge_alpha,
  width: T.arr.ridge_w,
  earlyOutPx: T.fig.early_out_px,
} as const;

export class WaterLinesPiece implements Piece {
  readonly id = 'water_lines' as const;
  /** The sea tier, so the piece stands in the layer under the beast. */
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The water the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['reading', 'zoomband', 'hello', 'resize'] as const;

  /**
   * Writes the shader term on.
   *
   * The gate has already answered the form factor, the zoom band, the
   * reading and the film, so a rebuild that runs at all is a rebuild that
   * wants the lines.
   */
  rebuild(ctx: ChartContext): void {
    ctx.scene.setWaterLines?.(true, WATER_LINE_CFG);
  }

  /** Nothing. The picture is a term inside the terrain pass. */
  emit(_sinks: ChartSinks, _ctx: ChartContext): void {
    // The lines are per-pixel. There is no instance to write.
  }
}

/** The one instance the registry holds, and the scene asks about. */
export const waterLines = new WaterLinesPiece();
